/**
 * Parse Frappe insert errors into field-level error map + toast message.
 *
 * Frappe's _server_messages is double-JSON-encoded:
 *   JSON.parse(JSON.parse(raw)) → string[]
 * Each string may itself be JSON with { message, indicator } or a plain string.
 */

export interface ParsedInsertError {
  /** Map of fieldname → error message for inline display */
  fieldErrors: Record<string, string>;
  /** Human-readable message for toast */
  toastMessage: string;
}

/**
 * Attempt to extract the inner message string from a Frappe message object.
 * Messages can be plain strings or JSON objects like { message: "...", indicator: "red" }.
 */
function extractMessage(raw: unknown): string {
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && typeof parsed.message === "string") {
        return parsed.message;
      }
    } catch {
      // not JSON, use as-is
    }
    return raw;
  }
  if (raw && typeof raw === "object" && "message" in raw && typeof (raw as any).message === "string") {
    return (raw as any).message;
  }
  return String(raw ?? "");
}

/**
 * Try to match a field name mentioned in an error message against known fields.
 * Returns the matched fieldname or null.
 */
function matchFieldName(message: string, fieldNames: string[]): string | null {
  const lower = message.toLowerCase();
  for (const fn of fieldNames) {
    // Match field name as a word boundary (e.g., "item_code" in "Duplicate entry for item_code")
    if (lower.includes(fn.toLowerCase())) {
      return fn;
    }
  }
  return null;
}

export function parseInsertError(
  error: unknown,
  fieldNames?: string[],
): ParsedInsertError {
  const fieldErrors: Record<string, string> = {};
  let toastMessage = "Failed to create item";
  const fields = fieldNames ?? [];

  try {
    if (!error || typeof error !== "object") {
      return { fieldErrors, toastMessage };
    }

    const err = error as Record<string, any>;

    // Extract main message
    if (typeof err.message === "string" && err.message) {
      toastMessage = err.message;
    }

    // Try _server_messages (double-JSON-encoded)
    if (typeof err._server_messages === "string") {
      try {
        const outer = JSON.parse(err._server_messages);
        const messages: unknown[] = Array.isArray(outer) ? outer : JSON.parse(outer);
        if (Array.isArray(messages)) {
          for (const raw of messages) {
            const msg = extractMessage(raw);
            if (!msg) continue;
            // Use last message as toast (most specific)
            toastMessage = msg;
            const matched = matchFieldName(msg, fields);
            if (matched) {
              fieldErrors[matched] = msg;
            }
          }
        }
      } catch {
        // Single JSON parse fallback
        try {
          const single = JSON.parse(err._server_messages);
          if (typeof single === "string") {
            toastMessage = single;
            const matched = matchFieldName(single, fields);
            if (matched) fieldErrors[matched] = single;
          }
        } catch {
          // Completely malformed, ignore
        }
      }
    }

    // Try .messages array (some Frappe error shapes)
    if (Array.isArray(err.messages)) {
      for (const raw of err.messages) {
        const msg = extractMessage(raw);
        if (!msg) continue;
        toastMessage = msg;
        const matched = matchFieldName(msg, fields);
        if (matched) fieldErrors[matched] = msg;
      }
    }

    // Handle DuplicateEntryError
    if (
      toastMessage.includes("Duplicate") ||
      toastMessage.includes("duplicate") ||
      err.exc_type === "DuplicateEntryError"
    ) {
      // Try to extract field from "Duplicate entry 'value' for key 'fieldname'" pattern
      const dupMatch = toastMessage.match(/for key ['"]?(\w+)['"]?/i);
      if (dupMatch) {
        const possibleField = dupMatch[1];
        const matched = matchFieldName(possibleField, fields);
        if (matched && !fieldErrors[matched]) {
          fieldErrors[matched] = toastMessage;
        }
      }
      // Also try matching any field name in the message
      if (Object.keys(fieldErrors).length === 0) {
        const matched = matchFieldName(toastMessage, fields);
        if (matched) fieldErrors[matched] = toastMessage;
      }
    }
  } catch {
    // Never crash — return generic toast
  }

  // Strip HTML tags from toast message for clean display
  toastMessage = toastMessage.replace(/<[^>]*>/g, "").trim() || "Failed to create item";

  return { fieldErrors, toastMessage };
}
